import { Logger } from "cy-logging";
import { IdentificationType } from "./models";
import { IdentificationTypeConfig, IdentificationTypeName } from "./constants";

/**
 * Minimal identification type shape returned by the API.
 */
export interface ApiIdentificationType {
  id: number;
  name: string;
}

export interface ResolveIdentificationTypesOptions {
  /** Identification types returned from the API */
  apiTypes: ApiIdentificationType[];
  /** Configuration specifying which types to use and their field requirements */
  config: IdentificationTypeConfig[];
  /** Logger instance for warning messages */
  logger: Logger;
}

export interface ResolveIdentificationTypesResult {
  /** Resolved identification types ready for use in forms */
  types: IdentificationType[];
  /** Names of configured types not found in API response */
  missingFromApi: string[];
  /** Names of API types not in configuration (informational) */
  notInConfig: string[];
}

/**
 * Resolves identification types by matching API results against configuration.
 *
 * Filters to only include types that are:
 * 1. Defined in the configuration (we know their field requirements)
 * 2. Present in the API response (we have a valid ID to use)
 *
 * Logs warnings for mismatches to aid debugging without throwing exceptions.
 *
 * @example
 * ```typescript
 * const { types } = resolveIdentificationTypes({
 *   apiTypes: await getIdentificationTypesServer(),
 *   config: SR_COMPLETE_REGISTRATION_FOREIGN_ID_TYPES,
 *   logger: createLoggerServer("identification"),
 * });
 * ```
 */
export function resolveIdentificationTypes(
  options: ResolveIdentificationTypesOptions,
): ResolveIdentificationTypesResult {
  const { apiTypes, config, logger } = options;

  const apiTypesByName = new Map(apiTypes.map((t) => [t.name, t]));
  const configuredNames = new Set<string>(config.map((c) => c.name));

  const missingFromApi = config
    .filter((c) => !apiTypesByName.has(c.name))
    .map((c) => c.name);

  const notInConfig = apiTypes
    .filter((t) => !configuredNames.has(t.name))
    .map((t) => t.name);

  if (missingFromApi.length > 0) {
    logger.warn("Identification types in config but not returned by API", {
      missingTypes: missingFromApi,
      configuredTypes: Array.from(configuredNames),
      apiReturnedTypes: apiTypes.map((t) => t.name),
    });
  }

  // Skip warning when config is empty — an empty config means
  // "no types needed", so unmatched API types are expected.
  if (notInConfig.length > 0 && config.length > 0) {
    logger.warn("Identification types returned by API but not in config", {
      unconfiguredTypes: notInConfig,
      configuredTypes: Array.from(configuredNames),
      apiReturnedTypes: apiTypes.map((t) => t.name),
    });
  }

  const types = config.flatMap((configItem) => {
    const apiType = apiTypesByName.get(configItem.name);
    if (!apiType) {
      return [];
    }
    return [
      {
        id: apiType.id,
        name: configItem.name,
        expiryDate: configItem.expiryDate,
        issueDate: configItem.issueDate,
        issuingCountry: configItem.issuingCountry,
        tempResidenceVisa: configItem.tempResidenceVisa,
      } satisfies IdentificationType,
    ];
  });

  return { types, missingFromApi, notInConfig };
}

/**
 * Finds a single identification type by name from an API response array.
 * Returns `undefined` if not found and logs a warning.
 *
 * Use this for single-type lookups such as finding "South African ID"
 * during the public registration flow.
 */
export function findIdentificationTypeByName(
  apiTypes: ApiIdentificationType[],
  name: IdentificationTypeName,
  logger: Logger,
): ApiIdentificationType | undefined {
  const found = apiTypes.find((t) => t.name === name);

  if (!found) {
    logger.warn("Identification type not found in API response", {
      searchedFor: name,
      availableTypes: apiTypes.map((t) => t.name),
    });
  }

  return found;
}
